import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import LogoutConfirmationModal from './modals/LogoutConfirmationModal';
import { FEATURES, FeatureKey } from '../config/featureFlags';
import { hasUserAccess } from '../utils/helperFunctions';

/**
 * LeftSidebar Component
 * 
 * A reusable sidebar component that provides navigation for the dashboard.
 * 
 * Props:
 * - currentPath: The current active route path (optional, defaults to '/devices')
 * 
 * Usage:
 * <LeftSidebar currentPath="/devices" />
 * 
 * Features:
 * - Logo with green circular design
 * - Navigation icons (Dashboard, Reports, Alerts, Share)
 * - Active state highlighting
 * - Logout functionality
 * - Profile picture placeholder
 */
interface LeftSidebarProps {
  currentPath?: string;
}

const LeftSidebar: React.FC<LeftSidebarProps> = ({ currentPath = '/devices' }) => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Force component re-mount on tab duplication
  const componentKey = React.useMemo(() => Date.now(), []);

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Still navigate to login even if logout fails
      navigate('/login');
    } finally {
      setIsLoggingOut(false);
      setShowLogoutModal(false);
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  const navigationItems = [
    {
      icon: `${process.env.PUBLIC_URL}/SideBarIcons/Devices.svg`,
      label: 'Devices',
      path: '/devices',
      allowedPaths: ['/devices','/manually-add-device','/add-device','/edit-device'],
      userAccess: true
    },
    {
      icon: `${process.env.PUBLIC_URL}/SideBarIcons/Reports.svg`,
      label: 'Reports',
      allowedPaths: ['/reports'],
      path: '/reports',
      userAccess: true
    },
    {
      icon: `${process.env.PUBLIC_URL}/SideBarIcons/Alerts.svg`,
      label: 'Alerts',
      allowedPaths: ['/alerts'],
      path: '/alerts',
      userAccess: true
    },
    {
      icon: `${process.env.PUBLIC_URL}/SideBarIcons/Share.svg`,
      label: 'Share',
      allowedPaths: ['/share'],
      path: '/share',
      userAccess: true
    },
    {
      icon: null,
      label: 'New Design',
      path: '/new-device-design',
      allowedPaths: ['/new-device-design'],
      isNewDesign: true,
      feature: 'newDesign' as FeatureKey,
      userAccess: true
    },
    {
      icon: `${process.env.PUBLIC_URL}/SideBarIcons/Alerts.svg`,
      label: 'NewAlerts',
      allowedPaths: ['/new-alerts'],
      path: '/new-alerts',
      feature: 'newAlerts' as FeatureKey,
      userAccess: true
    },
    {
      icon: `${process.env.PUBLIC_URL}/SVGIcons/solar_user-bold.svg`,
      label: 'Users',
      allowedPaths: ['/user-management'],
      path: '/user-management',
      feature: 'users' as FeatureKey,
      userAccess: hasUserAccess(user?.role),
    },
    {
      icon: `${process.env.PUBLIC_URL}/SideBarIcons/HelpSupportIcon.svg`,
      label: 'Help',
      allowedPaths: ['/help-support'],
      path: '/help-support',
      feature: 'helpSupport' as FeatureKey,
      userAccess: true
    },
  ].filter((item: any) => (!item.feature || FEATURES[item.feature as FeatureKey]) && item.userAccess);

  return (
    <div key={componentKey} className="w-20 min-h-full flex flex-col items-center py-6 px-2 ml-1 zoomOutLevel">
      {/* Logo */}
      <a 
        href="https://smarteco.ai/" 
        target="_blank" 
        rel="noopener noreferrer"
        className="pointer-events-none"
      >
        <img src={`${process.env.PUBLIC_URL}/SideBarIcons/GlobeLogo.svg`} alt="Logo" className="mb-14 mt-2 smarteco-logo w-[60px] h-[60px] pointer-events-auto cursor-pointer hover:opacity-80 transition-opacity"/>
      </a>

      {/* Navigation Icons */}
      <nav className="flex flex-col items-center space-y-2">
        {navigationItems.map((item: any) => {
          const isActive = item.allowedPaths.some((path: string) => currentPath === path || currentPath.startsWith(path + '/'));
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              title={item.label}
              className={`sidebar-button p-4 border border-[#FFFFFF4D] rounded-full flex items-center justify-center transition-colors ${
                isActive
                  ? 'bg-gradient-to-r from-[var(--gradient-button-left-color)] to-[var(--gradient-button-right-color)] border-none'
                  : 'hover:bg-gray-700'
              }`}
            >
              {item.isNewDesign ? (
                /* Sparkle / new-design icon */
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-white">
                  <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
                </svg>
              ) : (
                <img
                  src={item.icon}
                  alt={item.label}
                  className="w-7 h-7 filter brightness-0 invert"
                />
              )}
            </button>
          );
        })}
      </nav>

      {/* Bottom Section */}
      <div className="mt-auto flex flex-col items-center gap-4 border border-[#FFFFFF33] rounded-full">
        <button
          onClick={handleLogoutClick}
          className="w-12 h-12 rounded-full text-white hover:bg-gray-700 flex items-center justify-center transition-colors"
        >
          <LogOut className="h-5 w-5" />
        </button>
        
        {/* Profile Picture */}
        <button
          onClick={() => navigate('/settings')}
          className={`w-12 h-12 flex items-center justify-center ${!userData.profilePictureUrl && 'bg-gray-700'} rounded-full hover:bg-gray-700 transition-colors`}
        >
          {userData.profilePictureUrl ? (
            <img
              src={userData.profilePictureUrl}
              alt="Profile"
              className="rounded-full w-full h-full object-cover object-top"
            />
          ) : (
            <div className='rounded-full mx-auto'>
              <svg xmlns="http://www.w3.org/2000/svg" width={40} height={40} viewBox="0 0 640 640" fill='#fff'>
                <path d="M320 312C386.3 312 440 258.3 440 192C440 125.7 386.3 72 320 72C253.7 72 200 125.7 200 192C200 258.3 253.7 312 320 312zM290.3 368C191.8 368 112 447.8 112 546.3C112 562.7 125.3 576 141.7 576L498.3 576C514.7 576 528 562.7 528 546.3C528 447.8 448.2 368 349.7 368L290.3 368z" />
              </svg>
            </div>
          )}
        </button>
      </div>

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        isOpen={showLogoutModal}
        onClose={handleLogoutCancel}
        onConfirm={handleLogoutConfirm}
        isLoading={isLoggingOut}
      />
    </div>
  ); 
};

export default LeftSidebar;
